// React imports
// Ant design imports
import { Employee } from "@finway-group/shared/lib/models"
import { Button, Form, Modal } from "antd"
import { Store } from "antd/lib/form/interface"
import { parseCurrencyInput } from "Components/currencyInput/config"
// Component imports
import EmployeeForm from "Components/forms/employeeForm/employee.form"
import React, { useEffect } from "react"
// Other imports
import { useTranslation } from "react-i18next"
// Redux imports
import { useDispatch } from "react-redux"
import { useHistory, useParams } from "react-router-dom"
import { useCompany } from "Shared/hooks/company.hooks"
// Model imports
// Service imports
import { EmployeeService, NotificationService, UserService } from "Shared/services"
import { NotificationTypeEnum } from "Shared/services/notification.service"
// Store imports
import { ThunkDispatchResult } from "Shared/store"
import { createEmployee, fetchAllEmployees, inviteEmployee, updateEmployee } from "Shared/store/actions/employee/employeeActions"
// Imports of shared modules
import { copyDeeply, isEmptyString, removeStringWhiteSpaces, roundNumber, shouldRestoreItem, unsetApprovalFields } from "Shared/utils/helpers"
import useStateIfMounted from "Shared/utils/hooks/useStateIfMounted"

const { confirm, warning } = Modal

interface EmployeeFormInterface {
    isNew: boolean
    isShowing: boolean
    employee?: Employee
    onCancel: () => void
}

export const EmployeeModal: React.FC<EmployeeFormInterface> = ({ isNew, isShowing, employee, onCancel }: EmployeeFormInterface) => {
    const { t } = useTranslation()
    const { id } = useParams<{ id: string }>()

    const dispatch: ThunkDispatchResult = useDispatch()
    const history = useHistory()
    const loggedInUser = UserService.getLoggedInEmployeeProfile()

    const [isLoading, setIsLoading] = useStateIfMounted(false)
    const [isUploading, setIsUploading] = useStateIfMounted(false)
    const [isInviteMode, setIsInviteMode] = useStateIfMounted(false)
    const companyId = useCompany()?._id

    const [formInstance] = Form.useForm()

    useEffect(() => {
        // To show mileage rate as cents
        const adjustedMileageRate = employee?.activeCompanyProfile?.mileageRate ? roundNumber(employee.activeCompanyProfile.mileageRate * 100) : undefined
        if (!isNew && isShowing) formInstance.setFieldsValue({ ...employee, ...employee?.activeCompanyProfile, mileageRate: adjustedMileageRate })

        return () => {
            if (isShowing) formInstance.resetFields()
        }
    }, [isShowing])

    const handleInviteEmployee = async (invitedEmployee: any) => {
        try {
            await dispatch(inviteEmployee(invitedEmployee))
            NotificationService.send(NotificationTypeEnum.SUCCESS, t("notification:employee.invite.title"), t("notification:employee.invite.message"))
            handleHide()
        } catch (error) {
            setIsLoading(false)
            NotificationService.showErrorNotificationBasedOnResponseError(error, t("error:employee.invite.title"))
        }
    }

    const handleSubmit = async (values: Store) => {
        setIsLoading(true)

        if (isInviteMode) {
            if (values.approvals) values.approvals = values.approvals.map((approval: any) => ({ ...approval, limit: parseCurrencyInput(approval.limit) }))
            await handleInviteEmployee(values)
            return
        }

        let { approvals, superior, team, department, role, creditorNumber, mileageRate, ...restValues } = values
        const adjustedCreditorNumber = !isEmptyString(values.creditorNumber) ? values.creditorNumber : null

        approvals = approvals?.map((approval: any) => ({ ...approval, limit: parseCurrencyInput(approval.limit) }))

        const adjustedMileageRate = mileageRate ? Number(mileageRate) / 100 : null

        const employeeData = new Employee({
            ...restValues,
            img: !isNew
                ? employee?.img !== values.img
                    ? values.img.file
                        ? values.img.file.xhr
                        : values.img
                    : employee?.img
                : values.img && values.img?.file
                ? values.img.file.xhr
                : values.img,
            currentRole: employee?.getUserCompanyProfile(companyId)?.role, // auxiliary value to check if we are demoting a user to regular employee and disallow it if the user still has cost centers in charge
            ...(loggedInUser.activeCompanyProfile.isAdmin && {
                companyProfiles: isNew
                    ? [
                          {
                              companyId,
                              approvals: approvals || [],
                              role,
                              team,
                              department,
                              superior,
                              mileageRate: adjustedMileageRate,
                              creditorNumber: adjustedCreditorNumber,
                          },
                      ]
                    : employee?.companyProfiles?.map((userCompanyProfile) => {
                          if (userCompanyProfile.companyId === companyId) {
                              return {
                                  ...userCompanyProfile,
                                  approvals: approvals || [],
                                  role,
                                  team,
                                  department,
                                  superior,
                                  companyId,
                                  mileageRate: adjustedMileageRate,
                                  creditorNumber: adjustedCreditorNumber,
                              }
                          }

                          return userCompanyProfile
                      }),
            }),
        })

        if (employeeData.iban) employeeData.iban = removeStringWhiteSpaces(employeeData.iban)
        if (isNew) {
            try {
                await dispatch(createEmployee(employeeData))
                NotificationService.send(NotificationTypeEnum.SUCCESS, t("notification:employee.created.title"), t("notification:employee.created.message"))
                handleHide()
            } catch (err) {
                setIsLoading(false)
                // get all employees as otherwise the deleted ones are filterd out
                const conflictEmployee = EmployeeService.getEmployees().find((employeeFind: Employee) => employeeFind.email === employeeData.email)
                if (shouldRestoreItem(err, conflictEmployee)) {
                    restoreEmployee(copyDeeply(conflictEmployee!, Employee))
                } else {
                    NotificationService.showErrorNotificationBasedOnResponseError(err, t("error:employee.create.title"))
                }
            }
        } else {
            try {
                if (!loggedInUser.activeCompanyProfile.isAdmin) unsetApprovalFields(employeeData)

                const employeeId = id || (employee && employee.id)
                employeeId && (await dispatch(updateEmployee(employeeId, employeeData)))

                if (values.email !== employee?.email) warning({ title: t("info:email_verification_needed.title"), content: t("info:email_verification_needed.message") })

                handleHide()
                NotificationService.send(
                    NotificationTypeEnum.SUCCESS,
                    employeeId === loggedInUser.id ? t("notification:settings.user_profile.updated.title") : t("notification:employee.updated.title"),
                    employeeId === loggedInUser.id ? t("notification:settings.user_profile.updated.message") : t("notification:employee.updated.message")
                )
            } catch (err) {
                setIsLoading(false)
                NotificationService.showErrorNotificationBasedOnResponseError(err, t("error:employee.edit.title"))
            }
        }
    }

    const handleRestore = async (employeeToRestore: Employee) => {
        try {
            await dispatch(updateEmployee(employeeToRestore.id, employeeToRestore))
            NotificationService.send(NotificationTypeEnum.SUCCESS, t("notification:employee.restored.title"), t("notification:employee.restored.message"))
            handleHide()
            await dispatch(fetchAllEmployees())
            history.push(`/employees/${employeeToRestore.id}`)
        } catch (err) {
            setIsLoading(false)
            NotificationService.showErrorNotificationBasedOnResponseError(err, t("error:employee.edit.title"))
        }
    }

    const restoreEmployee = (employee: Employee) => {
        confirm({
            title: t("confirm:employee.restore.title"),
            content: t("confirm:employee.restore.message"),
            cancelText: t("confirm:employee.restore.cancel"),
            type: "warning",
            okText: t("confirm:employee.restore.confirm"),
            onOk: () => handleRestore(employee),
        })
    }

    const handleHide = () => {
        setIsLoading(false)
        setIsInviteMode(false)
        formInstance.resetFields()
        onCancel()
    }

    return (
        <Modal
            title={employee?.id === loggedInUser.id ? t("action:user_profile.edit") : isNew ? t("action:employee.create") : t("action:employee.edit")}
            visible={isShowing}
            maskClosable={false}
            onCancel={handleHide}
            cancelText={t("action:cancel")}
            confirmLoading={isLoading}
            destroyOnClose
            footer={[
                <Button
                    key="back"
                    onClick={() => {
                        formInstance.resetFields()
                        handleHide()
                    }}
                >
                    {t("action:cancel")}
                </Button>,
                <Button
                    key="submit"
                    type="primary"
                    loading={isLoading}
                    disabled={isUploading}
                    onClick={() => {
                        formInstance.submit()
                    }}
                >
                    {employee?.id === loggedInUser.id
                        ? t("action:user_profile.save")
                        : isNew
                        ? isInviteMode
                            ? t("action:employee.invite")
                            : t("action:employee.submit")
                        : t("action:employee.save")}
                </Button>,
            ]}
        >
            <EmployeeForm
                form={formInstance}
                isNew={isNew}
                employee={employee}
                isInviteMode={isInviteMode}
                onSubmit={handleSubmit}
                onRestore={restoreEmployee}
                setIsUploading={setIsUploading}
                setIsInviteMode={setIsInviteMode}
            />
        </Modal>
    )
}

export default React.memo(EmployeeModal)
